import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheFoundationStory() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-foundation-story" className="bg-background py-24 relative overflow-hidden" ref={ref}>
      <div className="w-full max-w-7xl mx-auto px-6">
        {}
        <div className={`mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-5xl md:text-6xl font-bold text-primary mb-6 tracking-tight">
            The Architecture of Aspiration
          </h2>
          <div className="h-1 w-32 bg-secondary rounded-full" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {}
          <div className={`lg:col-span-8 transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="prose prose-lg prose-headings:font-serif prose-p:text-muted-foreground max-w-none">
              {}
              <div className="md:columns-2 gap-12 space-y-0 text-justify">
                <p className="mb-6 first-letter:float-left first-letter:text-7xl first-letter:font-bold first-letter:text-secondary first-letter:mr-3 first-letter:mt-[-10px] first-letter:leading-none">
                  It began in 1978 with a simple yet powerful vision: to create
                  a platform where Hispanic entrepreneurs could thrive. What
                  started as a small gathering of local business leaders in
                  Denver has blossomed into the COHCC Foundation, a beacon of
                  economic empowerment for the entire state. The founders
                  understood that economic opportunity is not just about
                  business—it is about the stability of families, the vitality
                  of neighborhoods, and the future of our youth.
                </p>
                <p className="mb-6">
                  For over four decades, we have remained steadfast in our
                  mission to expand economic opportunity. Through shifting
                  economic landscapes and rapid demographic changes, the
                  Foundation has served as a bridge, connecting aspiring leaders
                  with the resources, mentorship, and capital necessary to turn
                  ambition into achievement. Our history is written in the
                  success stories of the thousands of individuals we have
                  supported.
                </p>
                <p className="mb-6">
                  Today, the Foundation stands as the nonprofit arm of the
                  Colorado Hispanic Chamber of Commerce, dedicated to a future
                  where Hispanic businesses are a driving force of the state's
                  economy. We are not just preserving a legacy; we are actively
                  building the infrastructure for the next generation of
                  innovators. From workforce development to youth education, our
                  programs are designed to meet the challenges of tomorrow while
                  honoring the resilience of our past.
                </p>
                <p>
                  As we look forward, our commitment deepens. We are investing
                  in data-driven insights to better understand our community's
                  needs and crafting strategic partnerships that amplify our
                  impact. The story of the COHCC Foundation is still being
                  written, and every donor, volunteer, and partner helps us
                  turn the page to a brighter, more prosperous chapter for all
                  Coloradans.
                </p>
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors duration-300">
                <Link to="/ImpactPage">
                  View Our Impact <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-accent hover:text-accent-foreground">
                <Link to="/GetInvolvedPage">
                  Join the Legacy
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className={`lg:col-span-4 flex flex-col gap-8 transition-all duration-700 delay-400 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {}
            <div className="relative group">
              <div className="absolute inset-0 bg-secondary/10 translate-x-4 translate-y-4 rounded-sm -z-10 transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2" />
              <div className="relative overflow-hidden rounded-sm shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/historical_founding_1978.png" alt="Historical photo of the chamber's founding members in 1978" className="w-full h-auto object-cover grayscale hover:grayscale-0 transition-all duration-700 ease-in-out" />
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-primary/90 to-transparent p-6 pt-12">
                  <span className="inline-block px-3 py-1 bg-secondary text-secondary-foreground text-xs font-bold tracking-wider uppercase mb-2">
                    Est. 1978
                  </span>
                  <p className="text-white font-serif text-lg leading-tight">
                    The founding members gathering in Denver to charter the organization.
                  </p>
                </div>
              </div>
            </div>

            {}
            <div className="border-l-4 border-secondary pl-6 py-2 bg-accent/30 pr-4 rounded-r-lg">
              <Quote className="size-8 text-secondary mb-2 opacity-50" />
              <p className="font-serif text-xl text-primary italic leading-relaxed">
                "We didn't just want a seat at the table; we wanted to build a
                table where everyone could feast."
              </p>
              <p className="mt-4 text-sm font-bold text-muted-foreground uppercase tracking-widest">
                — Founding Principle
              </p>
            </div>

            {}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-border/50 text-center">
                <span className="block text-3xl font-bold text-primary font-serif">45+</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Years of Service</span>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-border/50 text-center">
                <span className="block text-3xl font-bold text-secondary font-serif">10k+</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Lives Impacted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}